const EVENT_PREFIX = 'disfrazzes:';

function publish(name, payload = {}) {
  const analytics = window.Shopify?.analytics;

  if (typeof analytics?.publish !== 'function') return;

  try {
    const result = analytics.publish(`${EVENT_PREFIX}${name}`, payload);
    if (result && typeof result.catch === 'function') result.catch(() => {});
  } catch (_) {
    // Analytics must never affect storefront interactions.
  }
}

function text(value) {
  return typeof value === 'string' ? value.trim() : '';
}

function number(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function itemFromCard(card) {
  const itemId = text(card.dataset.analyticsItemId);
  if (!itemId) return null;

  const item = {
    item_id: itemId,
    item_name: text(card.dataset.analyticsItemName),
    item_brand: text(card.dataset.analyticsItemBrand),
    item_category: text(card.dataset.analyticsItemCategory),
    item_variant: text(card.dataset.analyticsItemVariant),
    variant_id: text(card.dataset.analyticsVariantId),
    price: number(card.dataset.analyticsItemPrice),
  };

  return Object.fromEntries(Object.entries(item).filter(([, value]) => value !== '' && value !== undefined));
}

function getList(card) {
  const grid = card.closest('[data-analytics-list-id]');
  if (!grid) return null;

  return {
    id: text(grid.dataset.analyticsListId),
    name: text(grid.dataset.analyticsListName),
    currency: text(grid.dataset.analyticsCurrency),
  };
}

const sentListSignatures = new Set();

function getAnalyticsCards(grid) {
  return [...grid.querySelectorAll('[data-analytics-item-id]')]
    .filter((card) => !card.closest('.swiper-slide-duplicate'));
}

function publishProductList(root = document) {
  const grids = root.matches?.('[data-analytics-list-id]')
    ? [root]
    : [...root.querySelectorAll?.('[data-analytics-list-id]') || []];

  grids.forEach((grid) => {
    const cards = getAnalyticsCards(grid);
    const items = cards.map(itemFromCard).filter(Boolean).map((item, index) => ({ ...item, index }));
    if (!items.length) return;

    const list = getList(cards[0]);
    if (!list?.id) return;

    const signature = `${list.id}:${items.map((item) => `${item.item_id}:${item.variant_id || ''}`).join(',')}`;
    if (sentListSignatures.has(signature)) return;
    sentListSignatures.add(signature);

    publish('product_list_viewed', {
      item_list_id: list.id,
      item_list_name: list.name,
      currency: list.currency,
      items,
    });
  });
}

function publishUiInteraction(action, element) {
  publish('ui_interaction', {
    ui_action: action,
    ui_element: text(element?.dataset?.analyticsUiElement) || (element?.tagName === 'A' ? 'link' : 'control'),
    ui_location: document.body.dataset.template || 'storefront',
    ui_label: text(element?.dataset?.analyticsUiLabel),
  });
}

function trackClick(event) {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;

  const productLink = target.closest('.product-card__link');
  if (productLink) {
    const card = productLink.closest('[data-analytics-item-id]');
    const item = card && itemFromCard(card);
    const list = card && getList(card);
    if (item && list?.id) {
      publish('product_selected', {
        item_list_id: list.id,
        item_list_name: list.name,
        currency: list.currency,
        items: [{ ...item, index: getAnalyticsCards(card.closest('[data-analytics-list-id]')).indexOf(card) }],
      });
    }
    return;
  }

  const interactive = target.closest('a, button, input, select');
  if (!interactive) return;

  if (interactive.matches('a[href^="tel:"]')) return publishUiInteraction('telephone_click', interactive);
  if (interactive.matches('a[href^="mailto:"]')) return publishUiInteraction('email_click', interactive);
  if (interactive.matches('[data-cart-trigger]')) return publishUiInteraction('cart_open', interactive);
  if (interactive.matches('.cart__view-cart-button')) return publishUiInteraction('cart_view_click', interactive);
  if (interactive.matches('.cart__checkout-button')) return publishUiInteraction('checkout_click', interactive);
  if (interactive.closest('facets-form-component')) return publishUiInteraction('filter_click', interactive);
  if (interactive.closest('[data-bundle-directory]')) return publishUiInteraction('bundle_filter_click', interactive);
  if (interactive.closest('.pagination, [data-testid="pagination"]')) return publishUiInteraction('pagination_click', interactive);
}

function trackChange(event) {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;

  if (target.closest('localization-form-component')) publishUiInteraction('localization_change', target);
  if (target.closest('facets-form-component')) publishUiInteraction('filter_change', target);
  if (target.matches('[data-bundle-theme-filter], [data-bundle-celebration-filter]')) {
    publishUiInteraction('bundle_filter_change', target);
  }
}

function trackDialog(event) {
  const dialog = event.target instanceof HTMLDialogElement ? event.target : null;
  if (!dialog?.open) return;

  publish('ui_impression', {
    element: dialog.id || dialog.getAttribute('aria-labelledby') || dialog.className,
  });
}

function trackConfirmedConversion(event) {
  const type = text(event.detail?.type);
  if (type === 'newsletter') {
    publish('newsletter_subscribed', { success: true });
  }
  if (type === 'sign_up') {
    publish('sign_up_completed', { success: true });
  }
}

function start() {
  publishProductList();
  document.addEventListener('click', trackClick);
  document.addEventListener('change', trackChange);
  document.addEventListener('toggle', trackDialog, true);
  document.addEventListener('disfrazzes:conversion-confirmed', trackConfirmedConversion);
  document.addEventListener('shopify:section:load', (event) => publishProductList(event.target));
  document.addEventListener('shopify:section:reorder', () => publishProductList());

  let scheduled = false;
  const observer = new MutationObserver(() => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      publishProductList();
    });
  });
  observer.observe(document.body, { childList: true, subtree: true });
}

window.DisfrazzesAnalytics = window.DisfrazzesAnalytics || { publish };

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', start, { once: true });
} else {
  start();
}
